import React, { useEffect, useState } from "react";
import {
  Flex,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Spinner,
  Box,
  useToast,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
} from "@chakra-ui/react";
import { ArrowBackIcon, EditIcon, InfoOutlineIcon, ChevronRightIcon, ChevronLeftIcon } from "@chakra-ui/icons";
import { useHistory, Link } from "react-router-dom";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import AlertApi from "api/alert"; 
import ProductApi from "api/product";

const PriceDifferences = () => {
  const history = useHistory();
  const toast = useToast();
  
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeAlert, setActiveAlert] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchAlerts(currentPage);
  }, [currentPage]);

  const fetchAlerts = async (page) => {
    setIsLoading(true);
    try {
      const response = await AlertApi.GetPriceDifferences(page);
      setAlerts(response.data.data || []);
      setTotalPages(response.data.last_page || 1);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      toast({ title: "خطا در دریافت اطلاعات", description: "مشکلی پیش آمد.", status: "error", duration: 3500, isClosable: true, position: "top-right" });
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const normalized = String(value).replace(/[^\d]/g, "");
    if (!normalized) return "";
    return normalized.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const getPlatformLabel = (platform) => {
    if (platform === 1 || platform === "1") return "PS4";
    if (platform === 2 || platform === "2") return "PS5";
    if (platform === 3 || platform === "3") return "PS4/PS5";
    return String(platform || "").toUpperCase();
  };

  const openEditModal = (alertGroup) => {
    setActiveAlert(alertGroup);
    setEditPrice(alertGroup.our_price ? String(alertGroup.our_price) : "");
    onOpen();
  };

  const handleSavePrice = async () => {
    if (!activeAlert) return;
    setIsSaving(true);
    try {
      const numericPrice = editPrice ? Number(editPrice.replace(/[^\d]/g, "")) : null;
      await ProductApi.UpdateAttribute(activeAlert.product_id, activeAlert.attribute_id, { price: numericPrice });
      toast({ title: "قیمت با موفقیت اصلاح شد", status: "success", duration: 2500, isClosable: true, position: "top-right" });
      onClose();
      fetchAlerts(currentPage); 
    } catch (error) {
      toast({ title: "خطا در بروزرسانی قیمت", status: "error", duration: 3500, isClosable: true, position: "top-right" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }} gap={6}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <CardHeader p="6px 0px 22px 0px" display="flex" justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" gap={3}>
            <Button size="sm" leftIcon={<ArrowBackIcon />} onClick={() => history.push("/admin/alerts")}>
              بازگشت
            </Button>
            <Text fontSize="xl" color={textColor} fontWeight="bold">
              اخطارهای قیمتی (اختلاف بیش از ۱۰٪)
            </Text>
          </Flex>
          <Badge colorScheme="red" p={2} borderRadius="md">
            کل ویژگی‌های مشکل‌دار: {totalItems}
          </Badge>
        </CardHeader>
        
        <CardBody>
          {isLoading ? (
            <Flex justify="center" align="center" py={10} w="100%">
              <Spinner size="xl" color="teal.500" />
            </Flex>
          ) : alerts.length === 0 ? (
            <Flex justify="center" align="center" py={10} w="100%">
              <Text color="green.500" fontSize="lg" fontWeight="bold">
                🎉 عالی! هیچ اختلاف قیمت فاحشی با رقبا وجود ندارد.
              </Text>
            </Flex>
          ) : (
            <Box overflowX="auto" w="100%">
              <Table variant="simple" minWidth="950px">
                <Thead>
                  <Tr>
                    <Th textAlign="right">نام محصول (بازی)</Th>
                    <Th textAlign="center">ظرفیت / پلتفرم</Th>
                    <Th textAlign="center">قیمت ما</Th>
                    <Th textAlign="center">لیست رقبای دارای اختلاف</Th>
                    <Th textAlign="center">عملیات</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {alerts.map((alertGroup) => (
                    <Tr key={alertGroup.attribute_id} _hover={{ bg: "gray.50" }}>
                      <Td>
                        {/* نام محصول بدون زیرخط و آیکون، فقط در حالت هاور مشخص می‌شود */}
                        <Text 
                          as={Link} 
                          to={`/admin/products/${alertGroup.product_id}/edit`}
                          fontWeight="bold" 
                          fontSize="sm" 
                          color={textColor}
                          _hover={{ color: "teal.500", textDecoration: "underline" }}
                        >
                          {alertGroup.product_name || "بدون نام"}
                        </Text>
                      </Td>

                      <Td textAlign="center">
                        <Badge colorScheme="blue" mx={1}>Z{alertGroup.z_type}</Badge>
                        <Badge colorScheme="gray" mx={1}>{getPlatformLabel(alertGroup.platform)}</Badge>
                      </Td>

                      <Td textAlign="center">
                        <Badge colorScheme="blue" variant="solid" px={3} py={2} fontSize="md">
                          {formatNumber(alertGroup.our_price)}
                        </Badge>
                      </Td>

                      <Td>
                        <Flex direction="column" gap={2} minW="260px">
                          {alertGroup.competitors.map((competitor) => {
                            const isLower = competitor.percent_diff < 0;
                            const priceColor = isLower ? "red.500" : "green.500";

                            return (
                              <Flex 
                                key={competitor.id} 
                                borderWidth="1px"
                                borderColor={borderColor}
                                borderRadius="md" 
                                px={3} py={1.5} 
                                display="flex" 
                                justifyContent="space-between" 
                                alignItems="center"
                              >
                                <Flex alignItems="center" gap={1.5}>
                                  <Text as="span" fontSize="sm" fontWeight="500">
                                    {competitor.competitor_name}
                                  </Text>
                                  
                                  <Tooltip hasArrow placement="top" label={
                                    <Box textAlign="right" dir="rtl" px={2} py={1} maxW="250px">
                                      <Text fontSize="xs" fontWeight="normal" mb={1.5}>
                                        {competitor.raw_label || "نام خام یافت نشد"}
                                      </Text>
                                      {competitor.checked_at && (
                                        <Flex justify="space-between" align="center" borderTop="1px solid" borderColor="gray.500" pt={1}>
                                          <Text fontSize="10px" color="gray.300">
                                            بررسی: {new Date(competitor.checked_at).toLocaleDateString('fa-IR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                          </Text>
                                          <Text fontSize="10px" fontWeight="bold" color={competitor.in_stock ? "green.300" : "red.300"}>
                                            {competitor.in_stock ? "موجود" : "ناموجود"}
                                          </Text>
                                        </Flex>
                                      )}
                                    </Box>
                                  }>
                                    <InfoOutlineIcon boxSize={2.5} color="gray.500" cursor="help" />
                                  </Tooltip>
                                </Flex>

                                <Flex dir="ltr" gap={2} alignItems="center">
                                  <Text as="span" fontSize="md" fontWeight="900" color={priceColor}>
                                    {formatNumber(competitor.competitor_price)}
                                  </Text>
                                  <Text as="span" fontSize="xs" fontWeight="bold" color={priceColor}>
                                    ({competitor.percent_diff > 0 ? `+${competitor.percent_diff}` : competitor.percent_diff}%)
                                  </Text>
                                </Flex>
                              </Flex>
                            );
                          })}
                        </Flex>
                      </Td>

                      <Td textAlign="center">
                        <Button size="sm" colorScheme="teal" leftIcon={<EditIcon />} onClick={() => openEditModal(alertGroup)}>
                          اصلاح قیمت
                        </Button>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              
              {totalPages > 1 && (
                <Flex justify="space-between" align="center" mt={6} pt={4} borderTopWidth="1px" borderColor="gray.200">
                  <Button 
                    size="sm" 
                    rightIcon={<ChevronRightIcon />} 
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                    isDisabled={currentPage === 1}
                  >
                    قبلی
                  </Button>
                  <Text fontSize="sm" fontWeight="bold">
                    صفحه {currentPage} از {totalPages}
                  </Text>
                  <Button 
                    size="sm" 
                    leftIcon={<ChevronLeftIcon />} 
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} 
                    isDisabled={currentPage === totalPages}
                  >
                    بعدی
                  </Button>
                </Flex>
              )}
            </Box>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent dir="rtl">
          <ModalHeader fontSize="lg">اصلاح قیمت ویژگی</ModalHeader>
          <ModalCloseButton right="unset" left="4" top="4" />
          
          <ModalBody pb={6}>
            {activeAlert && (
              <Flex direction="column" gap={5}>
                <Text fontWeight="bold" color="gray.600" fontSize="lg">
                  {activeAlert.product_name} 
                  <Badge mx={2} colorScheme="blue">Z{activeAlert.z_type}</Badge>
                  <Badge colorScheme="gray">{getPlatformLabel(activeAlert.platform)}</Badge>
                </Text>

                <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={3} bg="gray.50">
                  <Text fontSize="sm" fontWeight="bold" mb={2}>قیمت‌های رقبا برای تصمیم‌گیری:</Text>
                  <Flex direction="column" gap={3}>
                    {activeAlert.competitors.map((comp) => (
                      <Flex key={comp.id} justify="space-between" align="center" borderBottomWidth="1px" pb={2} borderColor="gray.200">
                        <Flex direction="column" gap={0.5}>
                          <Text fontSize="sm" color="gray.700" fontWeight="500">{comp.competitor_name}</Text>
                          <Text fontSize="xs" color={comp.in_stock ? "green.600" : "red.500"}>
                            {comp.in_stock ? "موجود" : "ناموجود"}
                          </Text>
                        </Flex>
                        
                        <Flex dir="ltr" gap={2} alignItems="center">
                          <Text fontSize="md" fontWeight="bold" color={comp.percent_diff < 0 ? "red.500" : "green.500"}>
                            {formatNumber(comp.competitor_price)}
                          </Text>
                          <Text fontSize="xs" fontWeight="bold" color={comp.percent_diff < 0 ? "red.500" : "green.500"}>
                            ({comp.percent_diff > 0 ? `+${comp.percent_diff}` : comp.percent_diff}%)
                          </Text>
                        </Flex>
                      </Flex>
                    ))}
                  </Flex>
                </Box>

                <FormControl mt={2}>
                  <FormLabel fontWeight="bold">تعیین قیمت جدید ما (تومان)</FormLabel>
                  <Input
                    type="text"
                    inputMode="numeric"
                    textAlign="left"
                    dir="ltr"
                    fontWeight="bold"
                    fontSize="xl"
                    size="lg"
                    borderColor="teal.400"
                    bg="white"
                    _focus={{ borderColor: "teal.500", boxShadow: "outline" }}
                    value={formatNumber(editPrice)}
                    onChange={(e) => setEditPrice(String(e.target.value).replace(/[^\d]/g, ""))}
                  />
                </FormControl>
              </Flex>
            )}
          </ModalBody>

          <ModalFooter bg="gray.50" borderTopWidth="1px">
            <Button variant="ghost" mr={3} onClick={onClose}>
              انصراف
            </Button>
            <Button colorScheme="teal" onClick={handleSavePrice} isLoading={isSaving} size="lg">
              ذخیره تغییرات
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Flex>
  );
};

export default PriceDifferences;